<template>
	<picture :ref="lazyImageLabel" class="picture-comp">
		<source
			v-for="set in sourceSets"
			:key="`${set.sourceProp.viewport}${set.imageProp.type}`"
			:type="set.imageProp.type"
			:media="set.media || undefined"
			:fetchpriority="fetchpriority"
			v-bind="{ [isVisible ? 'srcset' : 'data-srcset']: set.srcSet }"
		/>

		<!-- Fallback for non-supporting browsers -->
		<img
			v-image-fade-in="fadeIn"
			class="picture-comp__img"
			:class="{ 'position-top': objectPositionTop }"
			:alt="alt"
			:src="defaultImageUrl"
			:loading="loading"
			:fetchpriority="fetchpriority"
			@load="$emit('imageLoad')"
			@error="$emit('imageError', defaultImageUrl)"
		/>
	</picture>
</template>

<script setup lang="ts">
import type { ThumborImage } from '@/filters/thumbor';
import type { ImageProp } from '@/helpers/picture-comp-helper';

import { lazyImageLabel, useLazyImage } from '@/features/ui/composables/useLazyImage';
import PictureCompHelper from '@/helpers/picture-comp-helper';

import { computed } from 'vue';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';

const props = withDefaults(
	defineProps<{
		imageProfile: keyof ThumborImage<any>;
		alt?: string;
		imageUrl?: string;
		fadeIn?: boolean;
		slug?: string;
		lazy?: boolean;
		loading?: 'eager' | 'lazy';
		fetchpriority?: 'high' | 'auto';
		sponsoredRecImages?: PosterSponsoredRecImages | null;
		imagePropFilter?: string[];
		/* For image scaler this is not necessary, as an image with no .format still renders a default, but for bucket assets it is better to specify a default image format */
		defaultImageFormat?: 'avif' | 'webp' | 'jpeg';
		imageProps?: Record<string, ImageProp> | undefined;
		objectPositionTop?: boolean;
	}>(),
	{
		alt: '',
		imageUrl: '',
		fadeIn: false,
		slug: '',
		lazy: true,
		loading: 'eager',
		fetchpriority: 'auto',
		sponsoredRecImages: null,
		imagePropFilter: () => ['avif', 'webp', 'jpeg'],
		defaultImageFormat: 'jpeg',
		imageProps: undefined,
		objectPositionTop: false,
	}
);

defineEmits<{
	(e: 'imageLoad'): void;
	(e: 'imageError', src: string): void;
}>();

const { _el, isVisible } = useLazyImage(props.lazy);

const defaultImageUrl = computed(() => {
	if (props.sponsoredRecImages?.imageUrl) {
		return props.sponsoredRecImages.imageUrl;
	}

	return PictureCompHelper.getDefaultImageUrl(
		props.imageUrl,
		props.imageProfile,
		props.slug,
		props.defaultImageFormat
	);
});

const sourceSets = computed(() => {
	if (props.sponsoredRecImages?.sourceSets) {
		return props.sponsoredRecImages.sourceSets;
	}

	return PictureCompHelper.getSourceSetImages(
		props.imageUrl,
		props.imageProfile,
		props.imagePropFilter,
		props.imageProps
	);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.picture-comp {
	& {
		width: 100%;
		max-height: none; // needed to display backdrops in prerendered version (seo4ajax)
	}

	&__img {
		display: inline-block;
		width: 100%;
		height: 100%;
		object-fit: cover;

		&.position-top {
			object-position: 50% 15%;
		}
	}
}
</style>
